import React from 'react';
import { useData } from '../lib/DataContext.jsx';
import { KpiCard, SectionTitle, SrcBanner, Chip, ProvenanceFooter, ProvenanceBadge } from '../components/ui/kits.jsx';
import { TrendBars, TrendArea } from '../components/charts/charts.jsx';
import { fmt, fmtInt, TOOL_COLOR } from '../lib/format.js';

// Parse the first parseable "signature" number out of a focus-area metric list.
// Handles "27 lakh+", "14.92 lakh", "6.5 lakh+", "14,500+", "10,700+", "~70%", "2,600" etc.
function extractSignatureNumber(metrics) {
  for (const [val, label] of metrics || []) {
    if (typeof val !== 'string') continue;
    if (val.includes('%')) continue; // skip percentages — non-comparable
    const num = val.replace(/[+~,]/g, '').trim();
    const m = num.match(/^([\d.]+)\s*(lakh|crore|cr)?$/i);
    if (!m) continue;
    let n = parseFloat(m[1]);
    if (!Number.isFinite(n)) continue;
    const unit = (m[2] || '').toLowerCase();
    if (unit === 'lakh') n *= 100000;
    else if (unit === 'crore' || unit === 'cr') n *= 10000000;
    return { n, label, raw: val };
  }
  return null;
}

function fmtCompact(n) {
  if (n >= 1e7) return `${(n / 1e7).toFixed(n >= 1e8 ? 0 : 2)} Cr`;
  if (n >= 1e5) return `${(n / 1e5).toFixed(n >= 1e6 ? 0 : 2)} L`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}k`;
  return String(n);
}

export default function ReachImpact() {
  const { data } = useData();
  if (!data) return null;
  const r = data.reach;
  const k = r.kpis;

  return (
    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="page-reach">
      <SrcBanner source={r.source} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        <KpiCard testid="kpi-lives" hero label="Cumulative Lives" value={`${fmt(k.livesCr, 2)}`} unit=" cr" sub="Across the programme" />
        <KpiCard testid="kpi-spend" label="CSR Spend FY24-25" value={`₹${fmt(k.spendCr, 0)}`} unit=" Cr" sub="Highest in Parivartan history" />
        <KpiCard testid="kpi-national" label="National Footprint" value={`${k.states}`} sub={`States + ${k.uts} UTs`} />
        <KpiCard testid="kpi-rural" label="Rural Development" value={k.villages} sub={`${k.hrdpHouseholdsLakh} lakh HRDP households`} />
      </div>

      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="report" note="Decade of CSR spend from Annual Report" />}>CSR Spend · Decade of Growth</SectionTitle>
        <TrendBars data={r.spendTrend} suffix="" />
      </div>

      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="report" />}>Cumulative Lives Impacted</SectionTitle>
        <TrendArea data={r.livesTrend} suffix=" cr" />
      </div>

      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="report" />}>Signature Reach by Focus Area</SectionTitle>
        <FocusAreaComparison items={r.focusReach} />
      </div>

      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="report" />}>Reach by Focus Area</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
          {r.focusReach.map((fa) => (
            <div key={fa.area} style={{ padding: 14, border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface-2)' }} data-testid={`focus-${fa.area}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontWeight: 800, fontSize: 13 }}>{fa.area}</div>
                {fa.tool && <Chip color={TOOL_COLOR[fa.tool] || '#0a63b0'}>{fa.tool}</Chip>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {fa.metrics.map(([val, label], i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--primary)', minWidth: 70 }} className="tabular">{val}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ProvenanceFooter text={`Figures on this page are programme-wide reach from the ${r.source}. Distinct from the 186 OECD-assessed projects shown on the analytical pages.`} />
    </div>
  );
}

// Horizontal ranked bar comparing the primary "signature" metric per focus area.
function FocusAreaComparison({ items }) {
  const rows = (items || [])
    .map((fa) => ({ area: fa.area, tool: fa.tool, sig: extractSignatureNumber(fa.metrics) }))
    .filter((r) => r.sig)
    .sort((a, b) => b.sig.n - a.sig.n);
  const max = Math.max(1, ...rows.map((r) => r.sig.n));
  const toolFill = (t) => {
    const key = t === 'H&H' ? 'Health' : (t === 'PoE' ? 'POE' : t);
    return TOOL_COLOR[key] || 'var(--primary)';
  };
  const skipped = (items || []).filter((fa) => !extractSignatureNumber(fa.metrics));
  return (
    <div data-testid="focus-area-comparison" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((r) => {
        const pct = (r.sig.n / max) * 100;
        const color = toolFill(r.tool);
        return (
          <div key={r.area} style={{ display: 'grid', gridTemplateColumns: '220px 1fr 190px', gap: 12, alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{r.area}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>{r.tool || '—'}</div>
            </div>
            <div style={{ position: 'relative', height: 22, background: 'var(--line-2)', borderRadius: 6, overflow: 'hidden' }}>
              <div className="anim-grow" style={{
                width: `${pct}%`, height: '100%',
                background: `linear-gradient(90deg, ${color}, ${color}cc)`,
                borderRadius: 6,
              }} />
              <div style={{
                position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                color: pct > 30 ? '#fff' : 'var(--ink)', fontSize: 11.5, fontWeight: 800,
                textShadow: pct > 30 ? '0 1px 2px rgba(0,0,0,.15)' : 'none',
              }} className="tabular">
                {fmtCompact(r.sig.n)}
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', textAlign: 'right', lineHeight: 1.3 }}>
              <span className="tabular" style={{ fontWeight: 700, color: 'var(--ink)' }}>{r.sig.raw}</span>
              <div style={{ fontSize: 10.5, color: 'var(--ink-4)' }}>{r.sig.label}</div>
            </div>
          </div>
        );
      })}
      {skipped.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--ink-4)' }}>
          Excluded (no numeric signature): {skipped.map((s) => s.area).join(', ')}.
        </div>
      )}
    </div>
  );
}
